// Recovery helpers for a wedged MSAL redirect.
//
// The subtlety that makes this file necessary: we run with
// `cache.storeAuthStateInCookie: true`, and under that setting
// BrowserCacheManager mirrors every *temporary* cache entry into a cookie
// (`setTemporaryCache` -> `cookieStorage.setItem`) and then, on read, checks
// the cookie **before** sessionStorage. So clearing sessionStorage on its own
// is a no-op — the stale cookie keeps winning and the user stays stuck.
// Always expire the cookie too.
//
// The cookies MSAL writes here are session cookies (`setTemporaryCache` passes
// no `cookieLifeDays`), so they are shared across every tab of the profile and
// survive closing an individual tab. That is why a wedged
// `msal.interaction.status` does not clear itself by opening a new tab.

/** Written by MSAL as `msal.interaction.status` — no clientId is appended. */
export const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

const MSAL_KEY_PREFIX = 'msal.';

/** App-owned localStorage keys, cleared only by the hard reset. */
const APP_KEY_PREFIXES = ['chat_', 'rr_realty_'];
const APP_KEYS = ['chatSessions', 'currentChatId'];

/**
 * Expire one cookie. Deletion matches on name + path + domain only, so we
 * mirror the `path=/` MSAL wrote with and deliberately omit Secure/SameSite —
 * they play no part in matching, and adding Secure would make this silently
 * fail on a plain-http local dev origin.
 */
export function expireCookie(key: string, target: { cookie: string } = document): void {
  target.cookie = `${encodeURIComponent(key)}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/** Cookie names currently set on this origin. Values are not returned. */
export function readCookieNames(source: { cookie: string } = document): string[] {
  if (!source.cookie) return [];
  return source.cookie
    .split(';')
    .map((entry) => decodeURIComponent(entry.trim()).split('=')[0])
    .filter(Boolean);
}

/**
 * Clear MSAL's in-flight interaction state — cookies first, then sessionStorage.
 *
 * Scoped to temporary/interaction state on purpose: cached accounts and tokens
 * live in localStorage and are left alone, so recovering from a wedged redirect
 * does not sign the user out.
 *
 * @returns the keys actually cleared, for diagnostics.
 */
export function clearMsalInteractionState(): string[] {
  const cleared: string[] = [];

  // Cookie first — it takes precedence on read.
  for (const name of readCookieNames()) {
    if (name.startsWith(MSAL_KEY_PREFIX)) {
      expireCookie(name);
      cleared.push(`cookie:${name}`);
    }
  }
  // MSAL may not have mirrored the status key into a cookie yet; expiring an
  // absent cookie is harmless, and skipping it is not.
  expireCookie(MSAL_INTERACTION_STATUS_KEY);

  try {
    const stale = Object.keys(sessionStorage).filter((k) => k.startsWith(MSAL_KEY_PREFIX));
    for (const key of stale) {
      sessionStorage.removeItem(key);
      cleared.push(`session:${key}`);
    }
  } catch {
    // sessionStorage can throw in private-mode / blocked-storage contexts.
    // The cookie clear above is the load-bearing half.
  }

  return cleared;
}

/**
 * Clear MSAL's *entire* cache, including the account and token entries in
 * localStorage. This signs the user out, so it must only ever run behind a
 * clearly-labelled control.
 *
 * Needed because a corrupt localStorage entry (e.g. an `msal.account.keys`
 * value that no longer parses) makes MSAL's own `initialize()` throw.
 * MsalProvider catches that and does nothing with it, so `inProgress` never
 * leaves 'startup' and the app spins forever. Clearing interaction state alone
 * does not help — the bad entry is in localStorage, not the temporary cache.
 *
 * @returns the keys actually cleared, for diagnostics.
 */
export function clearMsalCache(): string[] {
  const cleared = clearMsalInteractionState();

  try {
    const doomed = Object.keys(localStorage).filter((key) => key.startsWith(MSAL_KEY_PREFIX));
    for (const key of doomed) {
      localStorage.removeItem(key);
      cleared.push(`local:${key}`);
    }
  } catch {
    // Best effort; the caller reloads regardless.
  }

  return cleared;
}

/**
 * Hard reset for the error screen: interaction state plus app-owned
 * localStorage (corrupt chat state is the likeliest cause of a render crash).
 * Signed-in accounts survive.
 */
export function clearAppState(): string[] {
  const cleared = clearMsalInteractionState();

  try {
    const doomed = Object.keys(localStorage).filter(
      (key) => APP_KEYS.includes(key) || APP_KEY_PREFIXES.some((p) => key.startsWith(p)),
    );
    for (const key of doomed) {
      localStorage.removeItem(key);
      cleared.push(`local:${key}`);
    }
  } catch {
    // Same rationale as above — best effort.
  }

  return cleared;
}
